import type { PlanogramItem, PlanogramItemOrientation } from "@CommonTypes/merchflow/pog/item";
import type { PlanogramContainer } from "@CommonTypes/merchflow/pog/container";
import type { PlanogramPegboard } from "@CommonTypes/merchflow/pog/pegboard";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { getCappingSpanCount } from "../../common/planogramFacing";
import { resolvePlanogramProductLabel } from "../../common/planogramItemLabel";
import { getPegboardHoleCounts } from "../../common/pegboardHoleCounts";
import { getPegboardXLeft } from "../../common/pegboardPosition";
import { getPegboardFacingChangeColor } from "../Pegboards/pegboardFacingChange";
import type {
  PlanogramProductCell,
  PlanogramProductCellKind,
  PlanogramProductLabel,
  PlanogramSceneContainer,
  PlanogramSceneDivider,
  PlanogramScene,
  PlanogramSceneHole,
  PlanogramScenePegboard,
  PlanogramScenePlacement,
  PlanogramScenePlacementCell,
  PlanogramSceneProduct,
  PlanogramSceneRack,
  PlanogramWorldRect,
} from "./types";
import { splitGroupsOf } from "@CommonUtils/pog/aisleSplits";
import { getOrientedDimensions } from "@CommonUtils/pog/orientedDimensions";
import { computeNeededSqueeze, withSqueezeRelief } from "@CommonUtils/pog/squeezeRelief";
import {
  getBayRacks,
  getBoardDrawing,
  isCollidingRackBoard,
  rackNameOf,
} from "@CommonUtils/pog/racks";
import {
  getDeadSpans,
  getMergedDeadSpans,
  getMergedMerchandisableWidth,
  getShelfItemWidth,
  getShelfItemXOffsets,
  type ShelfDeadSpan,
} from "@CommonUtils/pog/shelfMerchandising";
import {
  DEFAULT_DRAWN_DIVIDER_HEIGHT,
  getBetweenFacingsDividerWidth,
  getDividerDrawSize,
  getShelfItemDividerSlots,
} from "@CommonUtils/pog/shelfDividers";
import { getRunItemHeight } from "../../common/shelfDividerDraw";
import { itemFrontOverhangMarker, shelfFrontOverhangMarker } from "../../common/frontOverhangView";

/**
 * How wide the gap at a break in the run is drawn, in scene units — pog units,
 * hundredths of an inch, not screen pixels.
 *
 * Every view scales the whole scene, so the gap shrinks and grows with the
 * bays. Six inches is an eighth of a 48" bay: wide enough to read as a gap at
 * the scale that fits a long run on screen, without looking like a missing
 * bay when zoomed in. It stays in scene units, not screen pixels, so 2D, the
 * preview and 3D agree on where every bay is, and a pointer reads back as the
 * same pog x at any zoom.
 *
 * The gap lives in the renderers only. Pog space stays gapless — `getXLeft`
 * is unchanged — and the real gap is written into the PSA on export.
 */
export const SPLIT_GAP = 600;

/** Clear space kept between the divider, the caption and the next bay, in screen px. */
const AISLE_BREAK_CAPTION_PADDING_PX = 3;
const AISLE_BREAK_CAPTION_MAX_FONT_PX = 12;
/** Below this the caption is not legible, so it is left out and the divider alone marks the break. */
const AISLE_BREAK_CAPTION_MIN_FONT_PX = 8;

/**
 * Where the "Aisle break" caption goes, in screen pixels, for a gap that is
 * `gapScreenWidth` px wide on screen. It reads down the right half of the gap,
 * between the divider and the next bay, and never reaches into either bay.
 * Null when the gap is too narrow on screen to hold a legible caption.
 */
export const layoutAisleBreakCaption = (
  gapScreenWidth: number,
): { readonly centerOffset: number; readonly fontSize: number } | null => {
  const room = gapScreenWidth / 2 - 2 * AISLE_BREAK_CAPTION_PADDING_PX;
  const fontSize = Math.min(AISLE_BREAK_CAPTION_MAX_FONT_PX, room);
  if (fontSize < AISLE_BREAK_CAPTION_MIN_FONT_PX) return null;
  return { centerOffset: AISLE_BREAK_CAPTION_PADDING_PX + fontSize / 2, fontSize };
};

/** The part of a bay the gap arithmetic reads. */
interface PlanogramBayLike {
  readonly splitAfter?: boolean;
  readonly width: number;
  /** A pallet under a gondola bay answers with that bay; it takes no width along the run. */
  readonly getBayAbove?: () => unknown;
}

/**
 * True when the bay takes its own width along the run. A pallet under a
 * gondola bay stands where that bay does, so it adds nothing — the same rule
 * `PlanogramBay.getXLeft` follows.
 */
const takesRunWidth = (bay: PlanogramBayLike) => !bay.getBayAbove?.();

/**
 * True for every bay the run breaks after, aligned with `bays`. Read through
 * omni-common's `splitGroupsOf` so a split on the last bay is dropped here the
 * same way it is dropped everywhere else.
 */
export const getSplitSeams = (bays: readonly { splitAfter?: boolean }[]): boolean[] => {
  const groups = splitGroupsOf(bays);
  return bays.map(
    (_bay, bayIndex) =>
      groups[bayIndex + 1] !== undefined && groups[bayIndex + 1] !== groups[bayIndex],
  );
};

/**
 * Pog space is gapless; the scene the renderers draw is not. These two move a
 * horizontal position between them, so that anything written in pog units (a
 * container's edges, a pegboard's section x) lands where it belongs on the
 * stage, and a pointer on the stage reads back as the pog x it is over.
 *
 * On a run with no breaks both are the identity, so nothing changes.
 */
export interface PlanogramSplitOffsets {
  /** Where a pog x is drawn. */
  readonly toSceneX: (pogX: number) => number;
  /** Which pog x a point on the stage is over. A point inside a gap reads as the bay edge beside it. */
  readonly toPogX: (sceneX: number) => number;
}

interface BayOffset {
  readonly pogLeft: number;
  readonly pogRight: number;
  readonly sceneLeft: number;
  readonly shift: number;
}

export const createSplitOffsets = (bays: readonly PlanogramBayLike[]): PlanogramSplitOffsets => {
  const seams = getSplitSeams(bays);
  const offsets: BayOffset[] = [];
  let pogLeft = 0;
  let shift = 0;

  // Walk the run the way `getXLeft` and the scene geometry do: a pallet under a
  // gondola bay adds no width, so the bays after it keep their pog x.
  bays.forEach((bay, bayIndex) => {
    if (takesRunWidth(bay)) {
      offsets.push({ pogLeft, pogRight: pogLeft + bay.width, sceneLeft: pogLeft + shift, shift });
      pogLeft += bay.width;
    }
    if (seams[bayIndex]) shift += SPLIT_GAP;
  });

  const bayAt = (value: number, edgeOf: (offset: BayOffset) => number) => {
    let found: BayOffset | null = null;
    for (const offset of offsets) {
      if (edgeOf(offset) <= value) found = offset;
    }
    return found ?? offsets[0] ?? null;
  };

  return {
    toPogX: (sceneX) => {
      const offset = bayAt(sceneX, (candidate) => candidate.sceneLeft);
      if (!offset) return sceneX;
      // Inside a gap there is no pog x: the nearest bay edge is the honest answer.
      return Math.min(sceneX - offset.shift, offset.pogRight);
    },
    toSceneX: (pogX) => {
      const offset = bayAt(pogX, (candidate) => candidate.pogLeft);
      return offset ? pogX + offset.shift : pogX;
    },
  };
};

/** How far each bay is drawn right of its pog x, aligned with `bays`: the gaps before it. */
export const getSplitShifts = (
  bays: readonly (PlanogramBayLike & { readonly getXLeft: () => number })[],
): number[] => {
  const { toSceneX } = createSplitOffsets(bays);
  return bays.map((bay) => toSceneX(bay.getXLeft()) - bay.getXLeft());
};

/** The run's width as drawn: its pog width plus every gap. */
export const getDrawnRunWidth = (planogram: Pog["planogram"]) =>
  planogram.getWidth() + Math.max(0, ...getSplitShifts(planogram.bays));

const getIdentityFallback = (name: string, productCode: string, stableId: string) =>
  [name, productCode, stableId].find((value) => value.trim().length > 0) ?? stableId;

const getLabel = (product: object, label: PlanogramProductLabel, fallback: string) =>
  label === "none" ? fallback : resolvePlanogramProductLabel(product, label, fallback);

const getProductLabel = (item: PlanogramItem, label: PlanogramProductLabel) =>
  getLabel(item, label, getIdentityFallback(item.name, item.productCode, item.uniqueId));

const isNewInsertedItem = (item: PlanogramItem) =>
  item.updateStatus?.operation === "INSERT" && item.updateStatus?.changeType === "INSERTED";

interface CellPosition {
  column: number;
  row: number;
}

const createCell = (
  item: PlanogramItem,
  kind: PlanogramProductCellKind,
  orientation: PlanogramItemOrientation,
  position: CellPosition,
): PlanogramProductCell => ({
  column: position.column,
  height: item.getOrientedDimensionForOrientation("height", orientation),
  kind,
  orientation,
  row: position.row,
  unitsHigh: 1,
  unitsWide: 1,
  width: item.getOrientedDimensionForOrientation("width", orientation),
  x: 0,
  y: 0,
});

/**
 * The units a pack shows across and up when turned to `orientation`: one artwork tile each. A cap
 * is the position's own pack turned, so a tray x-cap shows the tray's units, not one stretched unit.
 */
const getPackUnitsShown = (item: PlanogramItem, orientation: PlanogramItemOrientation) => {
  if (item.merchandisingStyle === "UNIT") return { unitsHigh: 1, unitsWide: 1 };
  const grid =
    item.merchandisingStyle === "CASE"
      ? { width: item.unitsCaseWide, height: item.unitsCaseHigh, depth: item.unitsCaseDeep }
      : { width: item.unitsTrayWide, height: item.unitsTrayHigh, depth: item.unitsTrayDeep };
  const { width, height } = getOrientedDimensions(grid, orientation);
  return { unitsHigh: height, unitsWide: width };
};

/** `dividerWidth` spreads the facing columns apart: the divider stands between them. */
const projectProductCells = (item: PlanogramItem, dividerWidth = 0) => {
  const cells: PlanogramProductCell[] = [];
  const mainWidth = item.getWidth();
  const mainHeight = item.getHeight();
  const horizontalFacings = item.getHorizontalFacings();
  const verticalFacings = item.getVerticalFacings();
  const mainWidthSpan = mainWidth * horizontalFacings;
  const mainHeightSpan = mainHeight * verticalFacings;
  const columnPitch = mainWidth + dividerWidth;
  const xCapLeft =
    dividerWidth > 0 ? (horizontalFacings - 1) * columnPitch + mainWidth : mainWidthSpan;

  const yCapOrientation = item.yCapOrientation;
  const yCapWidth = item.getOrientedDimensionForOrientation("width", yCapOrientation);
  const yCapHeight = item.getOrientedDimensionForOrientation("height", yCapOrientation);
  const yCapColumns = getCappingSpanCount(mainWidthSpan, yCapWidth);
  const yCappingHeight = item.getYCappingHeight();

  if (item.yCapNum > 0) {
    for (let index = 0; index < item.yCapNum * yCapColumns; index += 1) {
      const column = Math.floor(index / item.yCapNum);
      const row = index % item.yCapNum;
      const cell = createCell(item, "y-cap", yCapOrientation, { column, row });

      cells.push({
        ...cell,
        height: yCapHeight,
        width: yCapWidth,
        x: column * yCapWidth,
        y: row * yCapHeight,
      });
    }
  }

  for (let index = 0; index < item.facings; index += 1) {
    const column = Math.floor(index / verticalFacings);
    const row = index % verticalFacings;
    const cell = createCell(item, "main", item.orientation, { column, row });

    cells.push({
      ...cell,
      height: mainHeight,
      width: mainWidth,
      x: column * columnPitch,
      y: yCappingHeight + row * mainHeight,
      unitsHigh: item.getUnitsHigh(),
      unitsWide: item.getUnitsWide(),
    });
  }

  const xCapOrientation = item.xCapOrientation;
  const xCapWidth = item.getOrientedDimensionForOrientation("width", xCapOrientation);
  const xCapHeight = item.getOrientedDimensionForOrientation("height", xCapOrientation);
  const xCapRows = getCappingSpanCount(mainHeightSpan, xCapHeight);
  const xCapTop = yCappingHeight + mainHeightSpan - xCapRows * xCapHeight;
  const xCapUnits = getPackUnitsShown(item, xCapOrientation);

  if (item.xCapNum > 0) {
    for (let index = 0; index < item.xCapNum * xCapRows; index += 1) {
      const column = Math.floor(index / xCapRows);
      const row = index % xCapRows;
      const cell = createCell(item, "x-cap", xCapOrientation, { column, row });

      cells.push({
        ...cell,
        ...xCapUnits,
        height: xCapHeight,
        width: xCapWidth,
        x: xCapLeft + column * xCapWidth,
        y: xCapTop + row * xCapHeight,
      });
    }
  }

  return cells;
};

type PegboardProjectionGeometry = Omit<PlanogramScenePegboard, "holes">;

const projectPegboardGeometry = (geometry: PegboardProjectionGeometry): PlanogramScenePegboard => {
  const counts = getPegboardHoleCounts({
    width: geometry.width,
    height: geometry.height,
    holesOffsetHorizontal: geometry.holeOffsetHorizontal,
    holesOffsetVertical: geometry.holeOffsetVertical,
    holesGapHorizontal: geometry.holeGapHorizontal,
    holesGapVertical: geometry.holeGapVertical,
  });
  const holes = Array.from({ length: counts.horizontal * counts.vertical }, (_, index) => {
    const column = index % counts.horizontal;
    const row = Math.floor(index / counts.horizontal);
    return {
      column,
      height: 2,
      row,
      width: 2,
      x: geometry.x + geometry.holeOffsetHorizontal + column * geometry.holeGapHorizontal - 1,
      y: geometry.y + geometry.holeOffsetVertical + row * geometry.holeGapVertical - 1,
    };
  });

  return { ...geometry, holes };
};

const projectPegboard = (
  pegboard: PlanogramPegboard,
  sceneHeight: number,
  width: number,
  toSceneX: PlanogramSplitOffsets["toSceneX"],
): PlanogramScenePegboard =>
  projectPegboardGeometry({
    bayId: pegboard.bay.uniqueId,
    height: pegboard.height,
    holeGapHorizontal: pegboard.holesGapHorizontal,
    holeGapVertical: pegboard.holesGapVertical,
    holeOffsetHorizontal: pegboard.holesOffsetHorizontal,
    holeOffsetVertical: pegboard.holesOffsetVertical,
    id: pegboard.uniqueId,
    source: "planogram",
    sourceShelfId: null,
    width,
    x: toSceneX(getPegboardXLeft(pegboard)),
    y: sceneHeight - pegboard.yBottom - pegboard.height,
  });

const projectHangcellPegboard = (
  shelf: PlanogramShelf,
  sceneHeight: number,
  toSceneX: PlanogramSplitOffsets["toSceneX"],
): PlanogramScenePegboard =>
  projectPegboardGeometry({
    bayId: shelf.bay.uniqueId,
    height: shelf.getHeight(),
    holeGapHorizontal: shelf.bay.width / 10,
    holeGapVertical: shelf.bay.width / 10,
    holeOffsetHorizontal: shelf.thickness * 2,
    holeOffsetVertical: shelf.thickness * 2,
    id: `hangcell-pegboard-${shelf.uniqueId}`,
    source: "syntheticHangcell",
    sourceShelfId: shelf.uniqueId,
    width: shelf.width,
    x: toSceneX(shelf.getXLeft()),
    y: sceneHeight - shelf.getYBottom(),
  });

const projectContainer = (
  container: PlanogramContainer,
  sceneHeight: number,
  toSceneX: PlanogramSplitOffsets["toSceneX"],
): PlanogramSceneContainer => ({
  color: container.color,
  height: container.getHeight(),
  id: container.uniqueId,
  label: container.getLabel() || container.uniqueId,
  shelfType: container.shelfType,
  width: container.getWidth(),
  x: toSceneX(container.xLeft),
  y: sceneHeight - container.yTop,
});

const projectPlacement = (
  pegboard: PlanogramPegboard,
  placement: PlanogramPegboard["placements"][number],
  sceneHeight: number,
  label: PlanogramProductLabel,
  toSceneX: PlanogramSplitOffsets["toSceneX"],
): PlanogramScenePlacement | null => {
  const pegboardXLeft = toSceneX(getPegboardXLeft(pegboard));
  const width = placement.pegProduct.unitWidth;
  const height = placement.pegProduct.unitHeight;
  const absoluteFacingCells = placement.holes.map<PlanogramScenePlacementCell>((hole) => ({
    depth: hole.depth,
    height,
    holeNoLeft: hole.holeNoLeft,
    holeNoTop: hole.holeNoTop,
    width,
    x:
      pegboardXLeft +
      pegboard.holesOffsetHorizontal +
      (hole.holeNoLeft - 1) * pegboard.holesGapHorizontal,
    y:
      sceneHeight -
      pegboard.yBottom -
      pegboard.height +
      pegboard.holesOffsetVertical +
      (hole.holeNoTop - 1) * pegboard.holesGapVertical,
  }));
  const firstFacingCell = absoluteFacingCells[0];
  if (!firstFacingCell) return null;

  const x = absoluteFacingCells.reduce(
    (minimum, cell) => Math.min(minimum, cell.x),
    firstFacingCell.x,
  );
  const y = absoluteFacingCells.reduce(
    (minimum, cell) => Math.min(minimum, cell.y),
    firstFacingCell.y,
  );
  const right = absoluteFacingCells.reduce(
    (maximum, cell) => Math.max(maximum, cell.x + cell.width),
    firstFacingCell.x + firstFacingCell.width,
  );
  const bottom = absoluteFacingCells.reduce(
    (maximum, cell) => Math.max(maximum, cell.y + cell.height),
    firstFacingCell.y + firstFacingCell.height,
  );
  const facingCells = absoluteFacingCells.map((cell) => ({
    ...cell,
    x: cell.x - x,
    y: cell.y - y,
  }));

  return {
    facingChangeColor: getPegboardFacingChangeColor(placement.pegProduct),
    facingChangeType: placement.pegProduct.updateStatus?.changeType ?? null,
    facingCells,
    height: bottom - y,
    id: placement.uniqueId,
    label: getLabel(
      placement.pegProduct,
      label,
      getIdentityFallback(
        placement.pegProduct.name,
        placement.pegProduct.productCode,
        placement.uniqueId,
      ),
    ),
    name: placement.pegProduct.name,
    pegboardId: pegboard.uniqueId,
    productCode: placement.pegProduct.productCode,
    productId: placement.pegProduct.id,
    width: right - x,
    x,
    y,
  };
};

interface ItemCoordinates {
  readonly xLeft: number;
  readonly xRight: number;
  readonly yBottom: number;
  readonly yTop: number;
}

const projectProduct = (
  item: PlanogramItem,
  coordinates: ItemCoordinates,
  shelfGroupId: string,
  sceneHeight: number,
  label: PlanogramProductLabel,
): PlanogramSceneProduct => {
  return {
    cells: projectProductCells(item, getBetweenFacingsDividerWidth(item.shelf.dividers)),
    frontOverhang: itemFrontOverhangMarker(item),
    height: coordinates.yTop - coordinates.yBottom,
    horizontalFacings: item.getHorizontalFacings(),
    id: item.uniqueId,
    isNewInsertedItem: isNewInsertedItem(item),
    isTray: item.merchandisingStyle === "TRAY",
    label: getProductLabel(item, label),
    name: item.name,
    orientation: item.orientation,
    productCode: item.productCode,
    shelfGroupId,
    shelfId: item.shelf.uniqueId,
    validOrientations: [...item.validOrientations],
    verticalFacings: item.getVerticalFacings(),
    width: coordinates.xRight - coordinates.xLeft,
    x: coordinates.xLeft,
    y: sceneHeight - coordinates.yTop,
  };
};

interface ShelfCoordinates {
  readonly xLeft: number;
  readonly yBottom: number;
  readonly yTop: number;
}

interface ProjectionGeometry {
  readonly bayXLeft: Map<string, number>;
  readonly dividers: PlanogramSceneDivider[];
  readonly itemCoordinates: Map<string, ItemCoordinates>;
  readonly shelfCoordinates: Map<string, ShelfCoordinates>;
  readonly shelfGroupIds: Map<string, string>;
  /** True for every bay the run breaks after, aligned with the pog's bays. */
  readonly splitSeams: readonly boolean[];
  readonly splitOffsets: PlanogramSplitOffsets;
  readonly coordinateWorkCount: number;
  readonly height: number;
  readonly width: number;
}

const createProjectionGeometry = (pog: Pog): ProjectionGeometry => {
  const bayXLeft = new Map<string, number>();
  const shelfCoordinates = new Map<string, ShelfCoordinates>();
  const shelfGroupIds = new Map<string, string>();
  const itemCoordinates = new Map<string, ItemCoordinates>();
  const dividers: PlanogramSceneDivider[] = [];
  let coordinateWorkCount = 0;
  let xLeft = 0;
  let maxItemYTop = 0;
  let maxItemXRight = 0;

  const splitSeams = getSplitSeams(pog.planogram.bays);
  const splitOffsets = createSplitOffsets(pog.planogram.bays);

  // A pallet under a gondola bay stands where that bay does, wherever it is
  // listed, and takes no width of its own along the run.
  const bayAboveOf = new Map(pog.planogram.bays.map((bay) => [bay.uniqueId, bay.getBayAbove()]));
  pog.planogram.bays.forEach((bay, bayIndex) => {
    if (takesRunWidth(bay)) {
      bayXLeft.set(bay.uniqueId, xLeft);
      xLeft += bay.width;
    }
    // The break is a gap in the drawing, so every bay after it moves along.
    if (splitSeams[bayIndex]) xLeft += SPLIT_GAP;
  });
  for (const bay of pog.planogram.bays) {
    const bayAbove = bayAboveOf.get(bay.uniqueId);
    if (bayAbove) {
      bayXLeft.set(bay.uniqueId, bayXLeft.get(bayAbove.uniqueId) ?? 0);
    }
    for (let index = 0; index < bay.shelves.length; index += 1) {
      const shelf = bay.shelves[index];
      if (!shelf) continue;
      const yBottom = shelf.getYBottom();
      shelfCoordinates.set(shelf.uniqueId, {
        xLeft: bayXLeft.get(bay.uniqueId) ?? 0,
        yBottom,
        // The shelf says where it ends: under the shelf above it, or, for a
        // pallet's lane, at the top of the pallet.
        yTop: shelf.getYTop(),
      });
    }
  }

  const allShelves = pog.planogram.bays.flatMap((bay) => bay.shelves);
  const shelvesById = new Map(allShelves.map((shelf) => [shelf.uniqueId, shelf]));
  const groupRoots = allShelves.filter((shelf) => !shelf.mergedLeft);
  for (const root of groupRoots) {
    const groupId = root.uniqueId;
    const groupShelves: typeof allShelves = [];
    const seen = new Set<string>();
    let shelf: (typeof allShelves)[number] | null = root;
    while (shelf && !seen.has(shelf.uniqueId)) {
      seen.add(shelf.uniqueId);
      groupShelves.push(shelf);
      shelfGroupIds.set(shelf.uniqueId, groupId);
      shelf = shelf.mergedRight ? (shelvesById.get(shelf.mergedRight.uniqueId) ?? null) : null;
    }

    const firstShelf = groupShelves[0];
    if (!firstShelf) continue;
    const firstCoordinates = shelfCoordinates.get(firstShelf.uniqueId);
    if (!firstCoordinates) continue;
    const mergedItems = groupShelves.flatMap((member) => member.items);
    const dividerSlots = getShelfItemDividerSlots(mergedItems);
    // A 2D divider stands as tall as the run's items, so it reads between products of any height.
    const runItemHeight = dividerSlots ? getRunItemHeight(mergedItems) : 0;
    const itemWidths = mergedItems.map((item, itemIndex) => {
      coordinateWorkCount += 1;
      return dividerSlots?.[itemIndex]?.width ?? getShelfItemWidth(item);
    });
    const itemXOffsets = getShelfItemXOffsets({
      itemWidths,
      merchandisableWidth: getMergedMerchandisableWidth(groupShelves),
      overflowPolicy: "extend-on-overflow",
      deadSpans: getMergedDeadSpans(groupShelves),
      // A divided shelf a pole cuts lays each piece out on its own (DEV-3536).
      pieces: dividerSlots?.map((slot) => slot.piece),
    });
    for (let itemIndex = 0; itemIndex < mergedItems.length; itemIndex += 1) {
      const item = mergedItems[itemIndex];
      coordinateWorkCount += 1;
      const itemWidth = itemWidths[itemIndex];
      const itemHeight = item.getHeight() * item.getVerticalFacings() + item.getYCappingHeight();
      const coordinates = shelfCoordinates.get(item.shelf.uniqueId);
      if (!coordinates) continue;
      const slotX = firstCoordinates.xLeft + itemXOffsets[itemIndex];
      const slot = dividerSlots?.[itemIndex];
      const itemX = slot ? slotX + slot.productX : slotX;
      const coordinatesForItem = {
        xLeft: itemX,
        xRight: slot ? itemX + slot.productWidth : itemX + itemWidth,
        yBottom: coordinates.yBottom + item.shelf.thickness,
        yTop: coordinates.yBottom + item.shelf.thickness + itemHeight,
      };
      itemCoordinates.set(item.uniqueId, coordinatesForItem);
      if (slot?.dividers) {
        const shelfBottom = coordinates.yBottom + item.shelf.thickness;
        const drawSize = getDividerDrawSize(
          slot.dividers,
          item.shelf.depth,
          DEFAULT_DRAWN_DIVIDER_HEIGHT,
        );
        const { width } = drawSize;
        const height = runItemHeight > 0 ? runItemHeight : drawSize.height;
        slot.dividerXs.forEach((x, dividerIndex) => {
          dividers.push({
            height,
            id: `${item.uniqueId}-divider-${dividerIndex}`,
            shelfId: item.shelf.uniqueId,
            width,
            x: slotX + x,
            y: shelfBottom + height,
          });
        });
      }
      shelfGroupIds.set(item.shelf.uniqueId, groupId);
      maxItemYTop = Math.max(maxItemYTop, coordinatesForItem.yTop);
      maxItemXRight = Math.max(maxItemXRight, coordinatesForItem.xRight);
    }
  }

  const structuralHeight = pog.planogram.getHeight();
  return {
    bayXLeft,
    coordinateWorkCount,
    dividers,
    height: Math.max(structuralHeight, maxItemYTop),
    itemCoordinates,
    shelfCoordinates,
    shelfGroupIds,
    splitOffsets,
    splitSeams,
    width: Math.max(xLeft, maxItemXRight),
  };
};

/**
 * How a board is drawn (see `getBoardDrawing`): not where a board beside it or
 * a rack over it fills its dead span. Each hatched span's zone runs up to the
 * lowest board live over that span. A rack board colliding with a board of its
 * bay (see `getBayRacks`) is flagged so the collision shows.
 */
const projectBoardDrawing = (shelf: PlanogramShelf, sceneHeight: number) => {
  const drawing = getBoardDrawing(shelf);
  const xLeft = shelf.getXLeft();
  const surface = shelf.getYBottom() + shelf.thickness;
  return {
    boardX: drawing.x,
    boardWidth: drawing.width,
    flaggedDeadSpans: drawing.flaggedDeadSpans.map((span) => {
      const ceiling = shelf.getCeilingAt(xLeft + span.x, xLeft + span.x + span.width);
      return {
        ...span,
        extensionHeight: Math.max(0, ceiling - surface),
        extensionY: sceneHeight - ceiling,
      };
    }),
    rackName: rackNameOf(shelf.assembly),
    rackCollides: isCollidingRackBoard(shelf),
  };
};

const projectBayRacks = (
  bay: Pog["planogram"]["bays"][number],
  bayX: number,
  sceneHeight: number,
): PlanogramSceneRack[] =>
  getBayRacks(bay).map((rack) => ({
    bayId: bay.uniqueId,
    boardIds: rack.boards.map((board) => board.uniqueId),
    height: rack.yTop - rack.yBase,
    id: `${bay.uniqueId}:${rack.key}`,
    labelHeight: rack.boards[0].thickness,
    name: rack.name,
    width: rack.xRight - rack.xLeft,
    x: bayX + (rack.xLeft - bay.getXLeft()),
    y: sceneHeight - rack.yTop,
  }));

interface UncachedProjection {
  /** Number of item coordinate/projection operations performed during geometry projection. */
  readonly coordinateCount: number;
  readonly scene: PlanogramScene;
}

const projectPlanogramSceneUncached = (
  pog: Pog,
  label: PlanogramProductLabel = "name",
): UncachedProjection =>
  withSqueezeRelief(computeNeededSqueeze(pog.planogram), () =>
    projectPlanogramSceneDrawn(pog, label),
  );

const projectPlanogramSceneDrawn = (pog: Pog, label: PlanogramProductLabel): UncachedProjection => {
  const geometry = createProjectionGeometry(pog);
  const { height } = geometry;
  const bays: PlanogramScene["bays"] = [];
  const containers: PlanogramScene["containers"] = pog.containers.map((container) =>
    projectContainer(container, height, geometry.splitOffsets.toSceneX),
  );
  const shelves: PlanogramScene["shelves"] = [];
  const fixtures: PlanogramScene["fixtures"] = [];
  const pegboards: PlanogramScenePegboard[] = [];
  const placements: PlanogramScenePlacement[] = [];
  const products: PlanogramScene["products"] = [];
  const splits: PlanogramScene["splits"] = [];
  const racks: PlanogramSceneRack[] = [];
  const unsupportedFixtureIds: string[] = [];

  for (let bayIndex = 0; bayIndex < pog.planogram.bays.length; bayIndex += 1) {
    const bay = pog.planogram.bays[bayIndex];
    if (!bay) continue;
    const bayX = geometry.bayXLeft.get(bay.uniqueId) ?? 0;
    const bayHeight = Math.max(bay.height, bay.notchOffset + bay.maxNotch * bay.notchSpacing);

    bays.push({
      baseHeight: bay.baseHeight,
      bayNo: bayIndex + 1,
      height: bayHeight,
      id: bay.uniqueId,
      maxNotch: bay.maxNotch,
      notchOffset: bay.notchOffset,
      notchSpacing: bay.notchSpacing,
      width: bay.width,
      x: bayX,
      y: height - bayHeight,
    });
    if (!bay.pallet) racks.push(...projectBayRacks(bay, bayX, height));

    if (geometry.splitSeams[bayIndex]) {
      splits.push({
        bayId: bay.uniqueId,
        bayNo: bayIndex + 1,
        height: bayHeight,
        id: `split-after-${bay.uniqueId}`,
        width: SPLIT_GAP,
        x: bayX + bay.width,
        y: height - bayHeight,
      });
    }

    for (const shelf of bay.shelves) {
      if (shelf.type === "HANGCELL") {
        pegboards.push(projectHangcellPegboard(shelf, height, geometry.splitOffsets.toSceneX));
      }
    }

    for (const pegboard of bay.pegboards) {
      pegboards.push(projectPegboard(pegboard, height, bay.width, geometry.splitOffsets.toSceneX));
      for (const placement of pegboard.placements) {
        const scenePlacement = projectPlacement(
          pegboard,
          placement,
          height,
          label,
          geometry.splitOffsets.toSceneX,
        );
        if (scenePlacement) placements.push(scenePlacement);
      }
    }

    // A pallet is seen from its front: the lanes behind are drawn first, so a
    // lane shows only where it rises above the ones in front of it.
    const shelvesInDrawingOrder = bay.pallet ? [...bay.shelves].reverse() : bay.shelves;
    for (const shelf of shelvesInDrawingOrder) {
      const shelfIndex = bay.shelves.indexOf(shelf);
      const coordinates = geometry.shelfCoordinates.get(shelf.uniqueId);
      if (!coordinates) continue;
      const shelfGroupId = geometry.shelfGroupIds.get(shelf.uniqueId) ?? shelf.uniqueId;
      if (shelf.type !== "REGULAR") {
        // HANGCELL products hang below the fixture (down to the shelf below, or bay floor);
        // RISER products sit above it (up to the shelf above, or bay top edge), same as REGULAR.
        // For HANGCELL this equals height/y below, since those already exclude the bar thickness.
        const nonMerchandisableExtensionHeight =
          shelf.type === "HANGCELL"
            ? shelf.getHeight()
            : coordinates.yTop - coordinates.yBottom - shelf.thickness;
        const nonMerchandisableExtensionY =
          shelf.type === "HANGCELL" ? height - coordinates.yBottom : height - coordinates.yTop;

        fixtures.push({
          ...projectBoardDrawing(shelf, height),
          bayId: bay.uniqueId,
          fixtureType: shelf.type,
          height: shelf.type === "HANGCELL" ? shelf.getHeight() : shelf.thickness,
          id: shelf.uniqueId,
          merchandisableWidth: shelf.getMerchandisableWidth(),
          deadSpans: getDeadSpans(shelf),
          nonMerchandisableExtensionHeight,
          nonMerchandisableExtensionY,
          shelfNo: shelfIndex + 1,
          thickness: shelf.thickness,
          width: shelf.width,
          x: coordinates.xLeft,
          y:
            shelf.type === "HANGCELL"
              ? height - coordinates.yBottom
              : height - coordinates.yBottom - shelf.thickness,
        });
        if (shelf.type !== "HANGCELL") {
          unsupportedFixtureIds.push(shelf.uniqueId);
        }
        if (shelf.type === "HANGCELL") {
          for (const item of shelf.items) {
            const itemCoordinates = geometry.itemCoordinates.get(item.uniqueId);
            if (itemCoordinates) {
              products.push({
                ...projectProduct(item, itemCoordinates, shelfGroupId, height, label),
                y: height - shelf.getYBottom() - shelf.thickness,
              });
            }
          }
        }
        continue;
      }

      shelves.push({
        frontOverhang: shelfFrontOverhangMarker(shelf),
        ...(bay.pallet
          ? {
              boardX: 0,
              boardWidth: shelf.width,
              flaggedDeadSpans: [],
              rackName: null,
              rackCollides: false,
            }
          : projectBoardDrawing(shelf, height)),
        bayId: bay.uniqueId,
        groupId: shelfGroupId,
        height: shelf.thickness,
        id: shelf.uniqueId,
        merchandisableWidth: shelf.getMerchandisableWidth(),
        deadSpans: getDeadSpans(shelf),
        // Extension zone beyond the shelf's own thickness: up to the shelf above, or bay top edge.
        nonMerchandisableExtensionHeight: coordinates.yTop - coordinates.yBottom - shelf.thickness,
        nonMerchandisableExtensionY: height - coordinates.yTop,
        shelfNo: shelfIndex + 1,
        thickness: shelf.thickness,
        width: shelf.width,
        x: coordinates.xLeft,
        y: height - coordinates.yBottom - shelf.thickness,
      });

      for (const item of shelf.items) {
        const itemCoordinates = geometry.itemCoordinates.get(item.uniqueId);
        if (itemCoordinates) {
          products.push(projectProduct(item, itemCoordinates, shelfGroupId, height, label));
        }
      }
    }
  }

  const productWidth = products.reduce(
    (maxWidth, product) => Math.max(maxWidth, product.x + product.width),
    0,
  );
  const placementWidth = placements.reduce(
    (maxWidth, placement) => Math.max(maxWidth, placement.x + placement.width),
    0,
  );

  return {
    coordinateCount: geometry.coordinateWorkCount,
    scene: {
      bays,
      containers,
      ...(geometry.dividers.length > 0 && {
        dividers: geometry.dividers.map((divider) => ({ ...divider, y: height - divider.y })),
      }),
      fixtures,
      height,
      pegboards,
      placements,
      products,
      racks,
      shelves,
      splits,
      unsupportedFixtureIds: [...new Set(unsupportedFixtureIds)],
      width: Math.max(geometry.width, productWidth, placementWidth),
    },
  };
};

export interface PlanogramSceneProjectionStats {
  readonly cacheEntryCount: number;
  /** Number of item coordinate/projection operations in the latest projection. */
  readonly coordinateCount: number;
  readonly createdEntityCount: number;
  readonly entityCount: number;
  readonly projectionCount: number;
  readonly reusedEntityCount: number;
}

export interface PlanogramSceneProjector {
  readonly getStats: () => PlanogramSceneProjectionStats;
  readonly project: (pog: Pog, label?: PlanogramProductLabel) => PlanogramScene;
}

type SceneEntity =
  | PlanogramScene["bays"][number]
  | PlanogramScene["containers"][number]
  | PlanogramScene["fixtures"][number]
  | NonNullable<PlanogramScene["pegboards"]>[number]
  | NonNullable<PlanogramScene["placements"]>[number]
  | PlanogramScene["products"][number]
  | PlanogramScene["shelves"][number];

interface CachedSceneEntity<T extends SceneEntity> {
  readonly signature: string;
  readonly value: T;
}

const getWorldRectSignature = ({ height, width, x, y }: PlanogramWorldRect) =>
  `${x},${y},${width},${height}`;

const getCellSignature = (cell: PlanogramProductCell) =>
  [
    cell.column,
    cell.height,
    cell.kind,
    cell.orientation,
    cell.row,
    cell.unitsHigh,
    cell.unitsWide,
    cell.width,
    cell.x,
    cell.y,
  ].join(",");

const getPlacementCellSignature = (cell: PlanogramScenePlacementCell) =>
  [cell.depth, cell.holeNoLeft, cell.holeNoTop, getWorldRectSignature(cell)].join(",");

const getHoleSignature = (hole: PlanogramSceneHole) =>
  [hole.column, hole.row, getWorldRectSignature(hole)].join(",");

const getDeadSpansSignature = (spans: readonly ShelfDeadSpan[]) =>
  spans.map((span) => `${span.x},${span.width}`).join(";");

const getBoardDrawingSignature = (
  entity: Pick<
    PlanogramScene["shelves"][number],
    "boardX" | "boardWidth" | "flaggedDeadSpans" | "rackName" | "rackCollides"
  >,
) =>
  [
    entity.boardX,
    entity.boardWidth,
    entity.rackName,
    entity.rackCollides,
    entity.flaggedDeadSpans
      .map((span) => `${span.x},${span.width},${span.extensionHeight},${span.extensionY}`)
      .join(";"),
  ].join("|");

const getSceneEntitySignature = (entity: SceneEntity): string => {
  const worldRect = getWorldRectSignature(entity);

  if ("cells" in entity) {
    return [
      worldRect,
      entity.horizontalFacings,
      entity.frontOverhang?.description,
      entity.isNewInsertedItem,
      entity.isTray,
      entity.label,
      entity.name,
      entity.orientation,
      entity.productCode,
      entity.shelfGroupId,
      entity.shelfId,
      entity.validOrientations.join(","),
      entity.verticalFacings,
      entity.cells.map(getCellSignature).join(";"),
    ].join("|");
  }

  if ("holes" in entity) {
    return [
      worldRect,
      entity.bayId,
      entity.holeGapHorizontal,
      entity.holeGapVertical,
      entity.holeOffsetHorizontal,
      entity.holeOffsetVertical,
      entity.source,
      entity.sourceShelfId,
      entity.holes.map(getHoleSignature).join(";"),
    ].join("|");
  }

  if ("pegboardId" in entity) {
    return [
      worldRect,
      entity.facingChangeColor,
      entity.facingChangeType,
      entity.facingCells.map(getPlacementCellSignature).join(";"),
      entity.label,
      entity.name,
      entity.pegboardId,
      entity.productCode,
      entity.productId,
    ].join("|");
  }

  if ("fixtureType" in entity) {
    return [
      worldRect,
      entity.bayId,
      entity.fixtureType,
      entity.merchandisableWidth,
      getDeadSpansSignature(entity.deadSpans),
      getBoardDrawingSignature(entity),
      entity.shelfNo,
      entity.thickness,
    ].join("|");
  }

  if ("groupId" in entity) {
    return [
      worldRect,
      entity.frontOverhang?.description,
      entity.bayId,
      entity.groupId,
      entity.merchandisableWidth,
      getDeadSpansSignature(entity.deadSpans),
      getBoardDrawingSignature(entity),
      entity.shelfNo,
      entity.thickness,
    ].join("|");
  }

  if ("shelfType" in entity) {
    return [worldRect, entity.color, entity.label, entity.shelfType].join("|");
  }

  return [
    worldRect,
    entity.baseHeight,
    entity.bayNo,
    entity.maxNotch,
    entity.notchOffset,
    entity.notchSpacing,
  ].join("|");
};

const reuseEntities = <T extends SceneEntity>(
  entities: readonly T[],
  cache: Map<string, CachedSceneEntity<T>>,
  stats: { createdEntityCount: number; reusedEntityCount: number },
): T[] =>
  entities.map((entity) => {
    const signature = getSceneEntitySignature(entity);
    const cached = cache.get(entity.id);
    if (cached?.signature === signature) {
      stats.reusedEntityCount += 1;
      return cached.value;
    }
    stats.createdEntityCount += 1;
    cache.set(entity.id, { signature, value: entity });
    return entity;
  });

const pruneEntityCache = <T extends SceneEntity>(
  entities: readonly T[],
  cache: Map<string, CachedSceneEntity<T>>,
) => {
  const liveIds = new Set(entities.map((entity) => entity.id));
  for (const id of cache.keys()) {
    if (!liveIds.has(id)) cache.delete(id);
  }
};

/**
 * Projects immutable POG snapshots while retaining unchanged entity entries by stable ID.
 * The input traversal is intentionally one pass over each projected collection; cache lookup
 * and signature work are bounded by the size of the entry being projected.
 */
export const createPlanogramSceneProjector = (): PlanogramSceneProjector => {
  const caches = {
    bays: new Map<string, CachedSceneEntity<PlanogramScene["bays"][number]>>(),
    containers: new Map<string, CachedSceneEntity<PlanogramScene["containers"][number]>>(),
    fixtures: new Map<string, CachedSceneEntity<PlanogramScene["fixtures"][number]>>(),
    pegboards: new Map<
      string,
      CachedSceneEntity<NonNullable<PlanogramScene["pegboards"]>[number]>
    >(),
    placements: new Map<
      string,
      CachedSceneEntity<NonNullable<PlanogramScene["placements"]>[number]>
    >(),
    products: new Map<string, CachedSceneEntity<PlanogramScene["products"][number]>>(),
    shelves: new Map<string, CachedSceneEntity<PlanogramScene["shelves"][number]>>(),
  };
  let lastPog: Pog | null = null;
  let lastLabel: PlanogramProductLabel | null = null;
  let lastScene: PlanogramScene | null = null;
  let stats: PlanogramSceneProjectionStats = {
    cacheEntryCount: 0,
    coordinateCount: 0,
    createdEntityCount: 0,
    entityCount: 0,
    projectionCount: 0,
    reusedEntityCount: 0,
  };

  const project = (pog: Pog, label: PlanogramProductLabel = "name") => {
    if (pog === lastPog && label === lastLabel && lastScene) return lastScene;

    const projectedResult = projectPlanogramSceneUncached(pog, label);
    const projected = projectedResult.scene;
    const counts = { createdEntityCount: 0, reusedEntityCount: 0 };
    const scene: PlanogramScene = {
      ...projected,
      bays: reuseEntities(projected.bays, caches.bays, counts),
      containers: reuseEntities(projected.containers, caches.containers, counts),
      fixtures: reuseEntities(projected.fixtures, caches.fixtures, counts),
      pegboards: reuseEntities(projected.pegboards ?? [], caches.pegboards, counts),
      placements: reuseEntities(projected.placements ?? [], caches.placements, counts),
      products: reuseEntities(projected.products, caches.products, counts),
      shelves: reuseEntities(projected.shelves, caches.shelves, counts),
    };
    pruneEntityCache(scene.bays, caches.bays);
    pruneEntityCache(scene.containers, caches.containers);
    pruneEntityCache(scene.fixtures, caches.fixtures);
    pruneEntityCache(scene.pegboards ?? [], caches.pegboards);
    pruneEntityCache(scene.placements ?? [], caches.placements);
    pruneEntityCache(scene.products, caches.products);
    pruneEntityCache(scene.shelves, caches.shelves);
    const entityCount =
      scene.bays.length +
      scene.containers.length +
      scene.fixtures.length +
      (scene.pegboards?.length ?? 0) +
      (scene.placements?.length ?? 0) +
      scene.products.length +
      scene.shelves.length;
    stats = {
      cacheEntryCount:
        caches.bays.size +
        caches.containers.size +
        caches.fixtures.size +
        caches.pegboards.size +
        caches.placements.size +
        caches.products.size +
        caches.shelves.size,
      coordinateCount: projectedResult.coordinateCount,
      createdEntityCount: counts.createdEntityCount,
      entityCount,
      projectionCount: stats.projectionCount + 1,
      reusedEntityCount: counts.reusedEntityCount,
    };
    lastLabel = label;
    lastPog = pog;
    lastScene = scene;
    return scene;
  };

  return { getStats: () => stats, project };
};

export const projectPlanogramScene = (
  pog: Pog,
  label: PlanogramProductLabel = "name",
): PlanogramScene => projectPlanogramSceneUncached(pog, label).scene;
